
/*
 * Hilltop Greenhouses - Pre-order Cart Guard
 * Theme: Eurus 9.6.0
 * Product tag: preorder
 *
 * Native product forms:
 *   data-hilltop-product-handle="product-handle"
 *
 * Custom Quick Order:
 *   HilltopPreorder.addItems(items)
 */

(() => {
  "use strict";

  const TAG = "preorder";
  const ROOT = window.Shopify?.routes?.root || "/";

  const MESSAGE =
    "Pre-order products must be ordered separately " +
    "from regular products. Please complete your " +
    "current order or empty your cart before adding " +
    "a different product type.";

  const productCache = new Map();

  function endpoint(path) {
    return ROOT + path;
  }

  async function getJSON(url) {
    const response = await fetch(url, {
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error("Shopify request failed: " + response.status);
    }

    return response.json();
  }

  async function getProduct(handle) {
    if (!productCache.has(handle)) {
      const request = getJSON(
        endpoint("products/" + encodeURIComponent(handle) + ".js")
      ).catch(error => {
        productCache.delete(handle);
        throw error;
      });

      productCache.set(handle, request);
    }

    return productCache.get(handle);
  }

  function isPreorder(product) {
    const tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags || "").split(",");

    return tags.some(
      tag => String(tag).trim().toLowerCase() === TAG
    );
  }

  async function cartTypes() {
    const cart = await getJSON(endpoint("cart.js"));

    if (!cart.items.length) {
      return { preorder: false, regular: false };
    }

    const handles = [
      ...new Set(cart.items.map(item => item.handle))
    ];

    const products = await Promise.all(
      handles.map(handle => getProduct(handle))
    );

    return {
      preorder: products.some(isPreorder),
      regular: products.some(product => !isPreorder(product))
    };
  }


function showError(message = MESSAGE, form = null) {
  // Find the product form associated with this error.
  const productForm =
    form ||
    document.querySelector(
      'form[data-hilltop-product-handle]'
    );

  if (!productForm) {
    console.warn(
      'Hilltop: Cannot locate product form for inline error.'
    );
    return;
  }

  // Reuse the message container if it already exists.
  let errorBox = productForm.querySelector(
    '.hilltop-preorder-inline-error'
  );

  if (!errorBox) {
    errorBox = document.createElement('div');
    errorBox.className = 'hilltop-preorder-inline-error';
    errorBox.setAttribute('role', 'alert');
    errorBox.setAttribute('aria-live', 'assertive');

    // Find the add-to-cart button.
    const addButton =
      productForm.querySelector(
        'button[type="submit"]'
      ) ||
      productForm.querySelector(
        '[name="add"]'
      );

    if (addButton) {
      addButton.parentElement.insertAdjacentElement(
        'beforebegin',
        errorBox
      );
    } else {
      productForm.prepend(errorBox);
    }
  }

  errorBox.textContent = message;
  errorBox.hidden = false;

  // Bring the error into view.
  errorBox.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest'
  });
}

function clearError(form) {
  form?.querySelector(
    '.hilltop-preorder-inline-error'
  )?.remove();
}


  async function validateItems(items) {
    const ids = items.map(item => String(item.id));
    const types = await cartTypes();

    // Resolve variant IDs against the products used in
    // this specific add-to-cart request.
    // Quick Order should provide product handles.
    const handles = [...new Set(items.map(item => item.handle))];

    if (handles.some(handle => !handle)) {
      throw new Error("Missing product handle.");
    }

    const products = await Promise.all(
      handles.map(handle => getProduct(handle))
    );

    const requestedPreorder = products.some(isPreorder);
    const requestedRegular = products.some(p => !isPreorder(p));

    if (
      (requestedPreorder && requestedRegular) ||
      (requestedPreorder && types.regular) ||
      (requestedRegular && types.preorder) ||
      (types.preorder && types.regular)
    ) {
      return false;
    }

    return ids.length > 0;
  }

  let pending = false;

  async function addItems(items, options = {}) {
    if (pending) return false;

    pending = true;

    try {
      const valid = await validateItems(items);

      if (!valid) {
        showError();
        return false;
      }

      const payload = {
        items: items.map(({ id, quantity, properties,
          selling_plan }) => ({
          id,
          quantity: Number(quantity || 1),
          ...(properties ? { properties } : {}),
          ...(selling_plan ? { selling_plan } : {})
        }))
      };

      const response = await fetch(endpoint("cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        showError(result.description || "Unable to add products.");
        return false;
      }

      if (options.redirect !== false) {
        window.location.assign(endpoint("cart"));
      }

      return result;
    } catch (error) {
      console.error("Hilltop pre-order guard:", error);

      showError(
        "Unable to verify the product order type. " +
        "Please try again."
      );

      return false;
    } finally {
      pending = false;
    }
  }

  // Capture native Shopify product form submissions.
  document.addEventListener("submit", event => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(form.action, window.location.href);

    if (!/\/cart\/add\/?$/.test(action.pathname)) return;

    // Only intercept forms explicitly registered for
    // this guard, avoiding interference with unrelated forms.
    const handle =
      form.dataset.hilltopProductHandle ||
      form.closest("[data-hilltop-product-handle]")
        ?.dataset.hilltopProductHandle;

    if (!handle) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const data = new FormData(form);
    const id = data.get("id");
    const quantity = Number(data.get("quantity") || 1);

    if (!id || quantity <= 0) return;

    const properties = {};

    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.*)\]$/);

      if (match) {
        properties[match[1]] = value;
      }
    }

    addItems([{
      id: String(id),
      handle,
      quantity,
      properties,
      selling_plan: data.get("selling_plan") || undefined
    }]);
  }, true);

  window.HilltopPreorder = {
    addItems,
    validateItems,
    cartTypes,
    showError,
    clearError
  };
})();
